import { useId, useState } from 'react';
import { dateInputToIso, toDateInputValue } from '@physical-movies/core';
import { useI18n } from '../app/context';
import { Sheet } from './Sheet';
import { Button, Field, StarRating, TextArea } from './ui';
import styles from './watchSheet.module.css';

export interface WatchDraft {
  watchedAt: string;
  rating: number | null;
  note: string | null;
}

/**
 * The expandable half of watch logging. One tap on the detail page records
 * "just now"; this is for backdating, rating and leaving a note.
 *
 * The form seeds itself from `initial` exactly once, so the caller must give
 * it a `key` that changes each time the sheet is opened. That keeps a fresh
 * "now" on every new entry without an effect re-seeding state behind the
 * user's fingers.
 */
export function WatchSheet({
  open,
  onClose,
  initial,
  onSubmit,
  onDelete,
}: {
  open: boolean;
  onClose(): void;
  initial: WatchDraft;
  onSubmit(draft: WatchDraft): void | Promise<void>;
  onDelete?: () => void | Promise<void>;
}) {
  const { t } = useI18n();
  const fieldId = useId();

  const [date, setDate] = useState(() => toDateInputValue(new Date(initial.watchedAt)));
  const [rating, setRating] = useState<number | null>(initial.rating);
  const [note, setNote] = useState(initial.note ?? '');
  const [busy, setBusy] = useState(false);

  const today = toDateInputValue(new Date());
  const inFuture = date > today;

  const submit = async () => {
    if (inFuture) return;
    setBusy(true);
    try {
      await onSubmit({
        watchedAt: dateInputToIso(date),
        rating,
        note: note.trim() ? note.trim() : null,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t('watch.title')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={() => void submit()} disabled={busy || inFuture}>
            {busy ? t('common.saving') : t('watch.submit')}
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        <Field
          label={t('watch.when')}
          htmlFor={`${fieldId}-date`}
          error={inFuture ? t('watch.futureDate') : null}
        >
          <div className={styles.dateRow}>
            <input
              id={`${fieldId}-date`}
              type="date"
              className={styles.dateInput}
              value={date}
              max={today}
              onChange={(event) => setDate(event.target.value)}
            />
            <Button onClick={() => setDate(today)} disabled={date === today}>
              {t('watch.now')}
            </Button>
          </div>
        </Field>

        <Field
          label={t('watch.rating')}
          optional={t('common.optional')}
          htmlFor={`${fieldId}-rating`}
        >
          <div id={`${fieldId}-rating`}>
            <StarRating
              value={rating}
              onChange={setRating}
              label={t('watch.rating')}
              starLabel={(count) => t('watch.ratingValue', { count })}
            />
          </div>
        </Field>

        <Field label={t('watch.note')} optional={t('common.optional')} htmlFor={`${fieldId}-note`}>
          <TextArea
            id={`${fieldId}-note`}
            value={note}
            maxLength={500}
            placeholder={t('watch.notePlaceholder')}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>

        {onDelete && (
          <Button
            variant="danger"
            block
            onClick={() => {
              if (window.confirm(t('watch.deleteConfirm'))) void onDelete();
            }}
          >
            {t('watch.deleteEntry')}
          </Button>
        )}
      </div>
    </Sheet>
  );
}
